"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useSession } from "next-auth/react";

import { PageLayout } from "@/components/ui/PageLayout";
import { NoWalletPrompt } from "@/components/dashboard/NoWalletPrompt";
import { usePasskey } from "@/contexts/PasskeyContext";
import { useSmartAccountBalances } from "@/hooks/useSmartAccountBalances";
import { useTokenLogos } from "@/hooks/useTokenLogos";

import {
  PortfolioHeader,
  AssetAllocationCard,
  SmartAccountCard,
  FundAccountCard,
  StrategyCard,
  NewStrategyCard,
  AISection,
  ActivitySection,
  type ActiveStrategyItem,
} from "@/components/portfolio";

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function PortfolioSkeleton() {
  return (
    <div className="space-y-5 animate-pulse" aria-hidden="true">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="h-7 w-32 rounded" style={{ background: "var(--surface)" }} />
          <div className="h-4 w-48 rounded" style={{ background: "var(--surface)" }} />
        </div>
        <div className="space-y-1 text-right">
          <div className="h-3 w-24 rounded ml-auto" style={{ background: "var(--surface)" }} />
          <div className="h-8 w-36 rounded ml-auto" style={{ background: "var(--surface)" }} />
        </div>
      </div>
      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 320px" }}>
        <div className="space-y-4">
          <div className="h-52 rounded-xl" style={{ background: "var(--surface)" }} />
        </div>
        <div className="h-72 rounded-xl" style={{ background: "var(--surface)" }} />
      </div>
      <div className="h-20 rounded-xl" style={{ background: "var(--surface)" }} />
      <div className="h-56 rounded-xl" style={{ background: "var(--surface)" }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PortfolioPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const { contractId, isConnected } = usePasskey();
  const { getLogoBySymbol } = useTokenLogos();

  // Redirect unauthenticated users
  useEffect(() => {
    if (status === "unauthenticated") {
      router.replace("/auth");
    }
  }, [status, router]);

  const isLoading = status === "loading";
  const hasWallet = session?.user.hasWallet === true;

  // Live contractId from PasskeyContext wins over stale session value
  const smartAccountAddress = contractId ?? session?.user.smart_account_address;
  const hasSmartAccount = Boolean(smartAccountAddress);

  const { balances, refresh: refreshBalances } = useSmartAccountBalances(smartAccountAddress);

  // Computed totals
  const totalAvailable = balances.usdc + balances.xlm * balances.xlmPrice;
  const totalInStrategies = balances.aquaLpValue + balances.soroswapLpValue;
  const totalPortfolioValue = totalAvailable + totalInStrategies;

  // Build active strategies from on-chain LP balances
  const activeStrategies: ActiveStrategyItem[] = [];

  if (balances.aquaLp > 0 || balances.soroswapLp > 0) {
    const weightedApy =
      balances.aquaLpValue + balances.soroswapLpValue > 0
        ? (balances.aquaLpValue * 4.8 + balances.soroswapLpValue * 5.2) /
          (balances.aquaLpValue + balances.soroswapLpValue)
        : 5.0;

    activeStrategies.push({
      id: "FIXED",
      name: "FIXED — Stablecoins",
      badge: "FIXED",
      description: "Low-risk stablecoin pools across Aqua and Soroswap",
      tokens: [{ symbol: "USDC" }, { symbol: "XLM" }],
      apy: `${weightedApy.toFixed(1)}%`,
      balanceUsd: balances.aquaLpValue + balances.soroswapLpValue,
    });
  }

  if (isLoading) {
    return (
      <PageLayout>
        <PortfolioSkeleton />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Responsive grid column definitions */}
      <style>{`
        .portfolio-main-grid {
          display: grid;
          grid-template-columns: 3fr 2fr;
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 767px) {
          .portfolio-main-grid { grid-template-columns: 1fr; }
        }
        .portfolio-strategy-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 12px;
        }
      `}</style>

      <div className="flex flex-col gap-8">
        {/* 1. Header */}
        <div className="anim-fadeSlideInUp-500ms">
          <PortfolioHeader
            email={session?.user.email}
            totalValue={totalPortfolioValue}
            hasSmartAccount={hasSmartAccount}
          />
        </div>

        {/* 2. Two-column: Asset Allocation (left) + Smart Account + Fund (right) */}
        <div
          className="portfolio-main-grid anim-fadeSlideInUp-500ms"
          style={{ animationDelay: "0.05s" }}
        >
          {/* LEFT: Asset Allocation */}
          <div className="min-w-0">
            {hasSmartAccount ? (
              <AssetAllocationCard balances={balances} getLogoBySymbol={getLogoBySymbol} />
            ) : (
              !hasWallet && !isConnected && <NoWalletPrompt />
            )}
          </div>

          {/* RIGHT: Smart Account info + Fund Account */}
          <div className="flex flex-col gap-4 min-w-0">
            {hasSmartAccount && smartAccountAddress && (
              <>
                <SmartAccountCard smartAccountAddress={smartAccountAddress} />
                <FundAccountCard
                  smartAccountAddress={smartAccountAddress}
                  onSuccess={refreshBalances}
                />
              </>
            )}

            {/* No-wallet prompt in right column when not connected */}
            {!hasSmartAccount && !hasWallet && !isConnected && (
              <NoWalletPrompt />
            )}
          </div>
        </div>

        {/* 3. Active Strategies — full width */}
        <section
          className="anim-fadeSlideInUp-500ms"
          aria-label="Active strategies"
          style={{ animationDelay: "0.08s" }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2
                className="font-serif font-normal"
                style={{ fontSize: "1.1rem", color: "var(--text-primary)" }}
              >
                Active Strategies
              </h2>
              <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                {activeStrategies.length}
              </span>
            </div>
            <Link
              href="/strategies"
              className="text-[12px] transition-opacity hover:opacity-70 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]"
              style={{ color: "var(--accent)" }}
            >
              Browse all →
            </Link>
          </div>

          <div className="portfolio-strategy-grid">
            {activeStrategies.map((s, i) => (
              <StrategyCard
                key={s.id}
                strategy={s}
                getLogoBySymbol={getLogoBySymbol}
                animDelay={`${(i * 0.06).toFixed(2)}s`}
              />
            ))}
            <NewStrategyCard />
          </div>
        </section>

        {/* 4. AI Section — full width */}
        <div className="anim-fadeSlideInUp-500ms" style={{ animationDelay: "0.10s" }}>
          <AISection balances={balances} smartAccountAddress={smartAccountAddress} />
        </div>

        {/* 5. Recent Activity — full width */}
        <div className="anim-fadeSlideInUp-500ms" style={{ animationDelay: "0.14s" }}>
          <ActivitySection />
        </div>
      </div>
    </PageLayout>
  );
}
